/**
 * OAuth2 login/consent legs, via the control plane (/api/oauth).
 *
 * The browser never calls Hydra's admin API: it isn't public, and the
 * control plane is what binds the OAuth2 subject to the verified Kratos
 * session and mints the token claims (tenant, roles, email, truthful
 * email_verified). Accepting straight at Hydra would skip all of that.
 */

const CONTROL_PLANE_API = import.meta.env.VITE_CONTROL_PLANE_API || 'http://localhost:3000';

export interface ConsentRequest {
  challenge: string;
  requested_scope: string[];
  requested_access_token_audience: string[];
  skip: boolean;
  subject: string;
  client: {
    client_id: string;
    client_name?: string;
    logo_uri?: string;
    policy_uri?: string;
    tos_uri?: string;
  };
  tenant_id: string;
  roles: string[];
}

/** What the sign-in pages need to know about an application's sign-in. */
export interface LoginRequest {
  challenge: string;
  skip: boolean;
  client: {
    client_id: string;
    client_name?: string;
    /** The application's address, for "Return to <app>" if the request expires. */
    app_url?: string;
  };
  tenant_id: string;
  /** 'registration' when the application asked to open on account creation. */
  screen: 'login' | 'registration';
  /** Social sign-in providers the application permits; null = all. */
  allowed_providers: string[] | null;
  has_session: boolean;
  second_factor_required: boolean;
}

export interface RedirectResponse {
  redirect_to: string;
}

/** A failed login/consent call; `code` is e.g. session_aal2_required. */
export class OAuthApiError extends Error {
  constructor(
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'OAuthApiError';
  }
}

async function oauthApi<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${CONTROL_PLANE_API}/api/oauth${path}`, {
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new OAuthApiError(
      payload.message || `Authorization request failed (HTTP ${response.status})`,
      payload.code
    );
  }
  return (await response.json()) as T;
}

export class HydraService {
  /**
   * The pending sign-in behind a login_challenge. A challenge Hydra no longer
   * honours raises OAuthApiError code login_challenge_expired.
   */
  getLoginRequest(challenge: string): Promise<LoginRequest> {
    return oauthApi(`/login?login_challenge=${encodeURIComponent(challenge)}`);
  }

  /** Completes the login leg as the CURRENT Kratos session's identity. */
  acceptLogin(challenge: string, remember = false): Promise<RedirectResponse> {
    return oauthApi('/login/accept', { challenge, remember });
  }

  getConsentRequest(challenge: string): Promise<ConsentRequest> {
    return oauthApi(`/consent?consent_challenge=${encodeURIComponent(challenge)}`);
  }

  /** The control plane grants only scopes Hydra requested and mints the claims. */
  acceptConsent(challenge: string, grantScope: string[], remember = false): Promise<RedirectResponse> {
    return oauthApi('/consent/accept', { challenge, grant_scope: grantScope, remember });
  }

  rejectConsent(challenge: string, reason?: string): Promise<RedirectResponse> {
    return oauthApi('/consent/reject', { challenge, reason });
  }
}

export const hydraService = new HydraService();
